export const GUEST_EMAIL_DOMAIN = 'guest.communityforwardsf.org';
export const GUEST_USERNAME_PATTERN = /^[a-z0-9._-]{3,32}$/;
export const GUEST_MIN_PASSWORD_LENGTH = 12;

export const normalizeGuestUsername = (username) => String(username || '').trim().toLowerCase();

export const guestUsernameToEmail = (username) => {
  const normalized = normalizeGuestUsername(username);
  if (!normalized) {
    return '';
  }
  return `${normalized}@${GUEST_EMAIL_DOMAIN}`;
};

export const isGuestEmail = (email) => {
  if (!email) {
    return false;
  }
  return String(email).toLowerCase().endsWith(`@${GUEST_EMAIL_DOMAIN}`);
};

export const isGuestUser = (claims = {}) => claims.guest === true;

export const UNAUTHORIZED_AUTH_EMAIL_MESSAGE = 'Only @communityforwardsf.org accounts are allowed';

export const isAllowedAuthEmail = (email, claims = {}) => {
  if (isGuestUser(claims) || isGuestEmail(email)) {
    return true;
  }
  if (!email) {
    return false;
  }
  const domain = String(email).split('@')[1];
  return Boolean(domain) && domain.toLowerCase() === 'communityforwardsf.org';
};

export const guestPermissionsFromClaims = (claims = {}) => ({
  clientProfile: claims.guestClientProfile === true,
  addClient: claims.guestAddClient === true,
  programs: claims.guestPrograms === true
});

/** True when the signed-in user is a guest kiosk account. */
export const isGuestAccount = (user) => {
  if (!user) {
    return false;
  }
  return user.isGuest === true ||
    user.claims?.guest === true ||
    isGuestEmail(user.email);
};

export const validateGuestUsername = (username) => {
  const normalized = normalizeGuestUsername(username);
  if (!GUEST_USERNAME_PATTERN.test(normalized)) {
    throw new Error('Username must be 3-32 characters and use only lowercase letters, numbers, dots, underscores, or hyphens');
  }
  return normalized;
};

const GUEST_PROGRAM_ROUTES = {
  'Drop-In': '/drop-in',
  Coronado: '/coronado',
  Eddy: '/eddy',
  ETH: '/eth',
  'Women\'s Flex': '/womens-flex',
  AWP: '/awp',
  MRSC: '/mrsc'
};

/** Housing kiosk guests land here first when they have programs access. */
const GUEST_HOUSING_HOME_LOCATIONS = ['Coronado', 'Eddy'];

const normalizeGuestLocations = (locations) => {
  if (!locations) {
    return [];
  }
  if (Array.isArray(locations)) {
    return locations.filter(Boolean);
  }
  if (typeof locations === 'object') {
    return Object.keys(locations)
      .filter((key) => /^\d+$/.test(key))
      .sort((a, b) => Number(a) - Number(b))
      .map((key) => locations[key])
      .filter(Boolean);
  }
  return [];
};

export const getGuestHomePath = (claims = {}) => {
  const permissions = guestPermissionsFromClaims(claims);
  const locations = normalizeGuestLocations(claims.locations);

  if (permissions.programs) {
    const housingLocation = locations.find(
      (location) => GUEST_HOUSING_HOME_LOCATIONS.includes(location) && GUEST_PROGRAM_ROUTES[location]
    );
    if (housingLocation) {
      return GUEST_PROGRAM_ROUTES[housingLocation];
    }

    const firstLocation = locations.find((location) => GUEST_PROGRAM_ROUTES[location]);
    if (firstLocation) {
      return GUEST_PROGRAM_ROUTES[firstLocation];
    }
  }

  if (permissions.clientProfile) {
    return '/client-list';
  }
  if (permissions.addClient) {
    return '/add-client';
  }

  return '/client-list';
};
